// src/pages/finance-portal/estimates/EstimateDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, Card, CardContent, Chip,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Alert, CircularProgress, Divider, Tooltip, Grid,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TransformIcon from '@mui/icons-material/Transform';
import DeleteIcon from '@mui/icons-material/Delete';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT: { bg: '#fef3c7', color: '#92400e' },
    SENT: { bg: '#dbeafe', color: '#1e40af' },
    VIEWED: { bg: '#e0e7ff', color: '#3730a3' },
    ACCEPTED: { bg: '#dcfce7', color: '#166534' },
    PARTIALLY_CONVERTED: { bg: '#fef9c3', color: '#854d0e' },
    CONVERTED: { bg: '#d1fae5', color: '#065f46' },
    EXPIRED: { bg: '#fecaca', color: '#991b1b' },
    DECLINED: { bg: '#f3f4f6', color: '#6b7280' },
};

function formatCurrency(val, curr) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: curr || 'USD' }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '\u2014';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function EstimateDetail() {
    const navigate = useNavigate();
    const { estimateId } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const [estimate, setEstimate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Dialogs
    const [sendDialog, setSendDialog] = useState(false);
    const [sending, setSending] = useState(false);

    const [acceptDialog, setAcceptDialog] = useState(false);
    const [accepting, setAccepting] = useState(false);

    const [convertDialog, setConvertDialog] = useState(false);
    const [converting, setConverting] = useState(false);

    const [deleteDialog, setDeleteDialog] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const fetchEstimate = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axiosInstance.get(`/api/finance/estimates/${estimateId}`);
            if (res.data.success) {
                setEstimate(res.data.data);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load estimate');
        } finally {
            setLoading(false);
        }
    }, [estimateId]);

    useEffect(() => { fetchEstimate(); }, [fetchEstimate]);

    const handleSend = async () => {
        setSending(true);
        try {
            await axiosInstance.post(`/api/finance/estimates/${estimateId}/send`);
            setSendDialog(false);
            enqueueSnackbar('Estimate sent successfully', { variant: 'success' });
            fetchEstimate();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to send estimate', { variant: 'error' });
        } finally {
            setSending(false);
        }
    };

    const handleAccept = async () => {
        setAccepting(true);
        try {
            await axiosInstance.post(`/api/finance/estimates/${estimateId}/accept`);
            setAcceptDialog(false);
            enqueueSnackbar('Estimate accepted successfully', { variant: 'success' });
            fetchEstimate();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to accept estimate', { variant: 'error' });
        } finally {
            setAccepting(false);
        }
    };

    const handleConvert = async () => {
        setConverting(true);
        try {
            const res = await axiosInstance.post(`/api/finance/estimates/${estimateId}/convert`);
            setConvertDialog(false);
            enqueueSnackbar('Estimate converted to invoice successfully', { variant: 'success' });
            if (res.data.data?.invoice_id) {
                navigate(`/finance-portal/invoices/${res.data.data.invoice_id}`);
            } else {
                fetchEstimate();
            }
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to convert estimate', { variant: 'error' });
        } finally {
            setConverting(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete(`/api/finance/estimates/${estimateId}`);
            setDeleteDialog(false);
            enqueueSnackbar('Estimate deleted successfully', { variant: 'success' });
            navigate('/finance-portal/estimates');
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed to delete estimate', { variant: 'error' });
        } finally {
            setDeleting(false);
        }
    };

    const getStatusLabel = (status) => {
        if (!status) return '';
        return status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: ACCENT }} />
            </Box>
        );
    }

    if (error && !estimate) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: 4 }}>
                <Alert severity="error" sx={{ borderRadius: '12px', fontFamily }}>{error}</Alert>
                <Button onClick={() => navigate('/finance-portal/estimates')} sx={{ fontFamily, mt: 2, textTransform: 'none' }}>
                    Back to Estimates
                </Button>
            </Box>
        );
    }

    if (!estimate) return null;

    const statusStyle = STATUS_COLORS[estimate.status] || {};
    const customer = estimate.customer || {};
    const customerName = customer.company_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || '\u2014';
    const lineItems = estimate.line_items || [];
    const curr = estimate.currency || 'USD';

    const canSend = estimate.status === 'DRAFT';
    const canAccept = estimate.status === 'SENT' || estimate.status === 'VIEWED';
    const canConvert = estimate.status === 'ACCEPTED' || estimate.status === 'SENT' || estimate.status === 'VIEWED';
    const canEdit = estimate.status === 'DRAFT';
    const canDelete = estimate.status === 'DRAFT';

    // Calculate totals from line items
    const lineAmount = (li) => {
        const base = (parseFloat(li.quantity) || 0) * (parseFloat(li.unit_price) || 0);
        const discPct = parseFloat(li.discount_percent) || 0;
        return base - (base * discPct / 100);
    };

    const subtotal = lineItems.reduce((sum, li) => sum + lineAmount(li), 0);
    const lineTaxTotal = lineItems.reduce((sum, li) => {
        const amt = lineAmount(li);
        const rate = parseFloat(li.tax_rate) || 0;
        return sum + (amt * rate / 100);
    }, 0);
    const estimateTaxAmount = subtotal * (parseFloat(estimate.tax_rate) || 0) / 100;
    const totalTax = lineTaxTotal + estimateTaxAmount;
    const totalDiscount = parseFloat(estimate.discount_amount) || 0;
    const total = estimate.total != null ? estimate.total : (subtotal + totalTax - totalDiscount);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <IconButton onClick={() => navigate('/finance-portal/estimates')} sx={{ color: 'white', mr: 0.5 }}>
                                <ArrowBackIcon />
                            </IconButton>
                            <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <RequestQuoteIcon sx={{ fontSize: 28, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' } }}>
                                        {estimate.estimate_number || 'Estimate'}
                                    </Typography>
                                    <Chip
                                        label={getStatusLabel(estimate.status)}
                                        size="small"
                                        sx={{
                                            fontFamily, fontSize: '0.75rem', fontWeight: 700, height: 26,
                                            bgcolor: statusStyle.bg, color: statusStyle.color,
                                        }}
                                    />
                                </Box>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                    {customerName}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Action Buttons */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            {canEdit && (
                                <Button
                                    variant="contained"
                                    startIcon={<EditIcon />}
                                    onClick={() => navigate(`/finance-portal/estimates/${estimateId}/edit`)}
                                    sx={{
                                        fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                        bgcolor: 'white', color: ACCENT,
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                                    }}
                                >
                                    Edit
                                </Button>
                            )}
                            {canSend && (
                                <Button
                                    variant="contained"
                                    startIcon={<SendIcon />}
                                    onClick={() => setSendDialog(true)}
                                    sx={{
                                        fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                        bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                    }}
                                >
                                    Send
                                </Button>
                            )}
                            {canAccept && (
                                <Button
                                    variant="contained"
                                    startIcon={<CheckCircleIcon />}
                                    onClick={() => setAcceptDialog(true)}
                                    sx={{
                                        fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                        bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                    }}
                                >
                                    Accept
                                </Button>
                            )}
                            {canConvert && (
                                <Button
                                    variant="contained"
                                    startIcon={<TransformIcon />}
                                    onClick={() => setConvertDialog(true)}
                                    sx={{
                                        fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                        bgcolor: 'rgba(255,255,255,0.2)', color: 'white',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                    }}
                                >
                                    Convert to Invoice
                                </Button>
                            )}
                            {canDelete && (
                                <Tooltip title="Delete Estimate">
                                    <IconButton onClick={() => setDeleteDialog(true)} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fecaca' } }}>
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1200, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Converted Invoice Link */}
                {(estimate.status === 'CONVERTED' || estimate.status === 'PARTIALLY_CONVERTED') && estimate.converted_invoice_id && (
                    <Alert
                        severity="info"
                        icon={<ReceiptLongIcon />}
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button
                                size="small"
                                onClick={() => navigate(`/finance-portal/invoices/${estimate.converted_invoice_id}`)}
                                sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}
                            >
                                View Invoice
                            </Button>
                        }
                    >
                        <Typography sx={{ fontFamily, fontSize: '0.9rem' }}>
                            This estimate has been converted to Invoice{' '}
                            <Typography
                                component="span"
                                sx={{ fontFamily, fontWeight: 700, cursor: 'pointer', color: '#1e40af', textDecoration: 'underline' }}
                                onClick={() => navigate(`/finance-portal/invoices/${estimate.converted_invoice_id}`)}
                            >
                                {estimate.converted_invoice_number || `#${estimate.converted_invoice_id}`}
                            </Typography>
                        </Typography>
                    </Alert>
                )}

                {/* Info Cards */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    {/* Customer Info */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <PersonIcon sx={{ fontSize: 20, color: ACCENT }} />
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Customer</Typography>
                                </Box>
                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '1rem', color: '#1e293b', mb: 0.5 }}>
                                    {customerName}
                                </Typography>
                                {customer.email && (
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                        {customer.email}
                                    </Typography>
                                )}
                                {customer.phone && (
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                        {customer.phone}
                                    </Typography>
                                )}
                                {(customer.billing_address_line1 || customer.billing_city) && (
                                    <Box sx={{ mt: 1 }}>
                                        {customer.billing_address_line1 && (
                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>{customer.billing_address_line1}</Typography>
                                        )}
                                        {customer.billing_address_line2 && (
                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>{customer.billing_address_line2}</Typography>
                                        )}
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                            {[customer.billing_city, customer.billing_state, customer.billing_zip].filter(Boolean).join(', ')}
                                        </Typography>
                                        {customer.billing_country && (
                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>{customer.billing_country}</Typography>
                                        )}
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Estimate Info */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <CalendarTodayIcon sx={{ fontSize: 20, color: ACCENT }} />
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Estimate Info</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Estimate #</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{estimate.estimate_number || '\u2014'}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Date</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{formatDate(estimate.estimate_date)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Expiration</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{formatDate(estimate.expiration_date)}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>Currency</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{curr}</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Total */}
                    <Grid item xs={12} md={4}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <AttachMoneyIcon sx={{ fontSize: 20, color: ACCENT }} />
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Total</Typography>
                                </Box>
                                <Typography sx={{ fontFamily, fontSize: '2rem', fontWeight: 800, color: ACCENT, mb: 0.5 }}>
                                    {formatCurrency(total, curr)}
                                </Typography>
                                <Chip
                                    label={getStatusLabel(estimate.status)}
                                    size="small"
                                    sx={{
                                        fontFamily, fontSize: '0.75rem', fontWeight: 700, height: 26,
                                        bgcolor: statusStyle.bg, color: statusStyle.color,
                                    }}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Line Items */}
                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Line Items</Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 40 }}>#</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 90 }}>Type</TableCell>
                                        <TableCell sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase' }}>Description</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 70 }}>Qty</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 100 }}>Unit Price</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 70 }}>Tax %</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 80 }}>Discount %</TableCell>
                                        <TableCell align="right" sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', width: 110 }}>Amount</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {lineItems.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                                                <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>No line items</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : lineItems.map((li, idx) => {
                                        const amt = lineAmount(li);
                                        const typeLabel = li.item_type ? li.item_type.charAt(0) + li.item_type.slice(1).toLowerCase() : '\u2014';
                                        return (
                                            <TableRow key={li.id || idx} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>{idx + 1}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={typeLabel}
                                                        size="small"
                                                        sx={{
                                                            fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                            bgcolor: li.item_type === 'DISCOUNT' ? '#fef3c7' : '#f1f5f9',
                                                            color: li.item_type === 'DISCOUNT' ? '#92400e' : '#475569',
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: '#1e293b' }}>{li.description || '\u2014'}</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{li.quantity}</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{formatCurrency(li.unit_price, curr)}</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{li.tax_rate ? `${li.tax_rate}%` : '\u2014'}</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', color: '#475569' }}>{li.discount_percent ? `${li.discount_percent}%` : '\u2014'}</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(amt, curr)}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                {/* Totals Summary & Notes */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                    {/* Notes & Terms */}
                    <Grid item xs={12} md={7}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                {estimate.notes && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', mb: 0.5 }}>Notes</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', whiteSpace: 'pre-wrap' }}>{estimate.notes}</Typography>
                                    </Box>
                                )}
                                {estimate.terms && (
                                    <Box>
                                        <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', mb: 0.5 }}>Terms & Conditions</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#475569', whiteSpace: 'pre-wrap' }}>{estimate.terms}</Typography>
                                    </Box>
                                )}
                                {!estimate.notes && !estimate.terms && (
                                    <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#94a3b8' }}>No notes or terms added.</Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Totals */}
                    <Grid item xs={12} md={5}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>Summary</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Subtotal</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(subtotal, curr)}</Typography>
                                    </Box>
                                    {totalTax > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>
                                                Tax
                                                {(parseFloat(estimate.tax_rate) || 0) > 0 && ` (${estimate.tax_rate}%)`}
                                            </Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(totalTax, curr)}</Typography>
                                        </Box>
                                    )}
                                    {totalDiscount > 0 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#64748b' }}>Discount</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#ef4444' }}>-{formatCurrency(totalDiscount, curr)}</Typography>
                                        </Box>
                                    )}
                                    <Divider />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5 }}>
                                        <Typography sx={{ fontFamily, fontSize: '1.15rem', fontWeight: 700, color: '#1e293b' }}>Total</Typography>
                                        <Typography sx={{ fontFamily, fontSize: '1.15rem', fontWeight: 700, color: ACCENT }}>{formatCurrency(total, curr)}</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Send Confirmation Dialog */}
            <Dialog open={sendDialog} onClose={() => setSendDialog(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Send Estimate {estimate.estimate_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will send the estimate to the customer via email. The status will be updated to Sent.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setSendDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSend}
                        disabled={sending}
                        startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#3b82f6', '&:hover': { bgcolor: '#2563eb' } }}
                    >
                        {sending ? 'Sending...' : 'Send'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Accept Confirmation Dialog */}
            <Dialog open={acceptDialog} onClose={() => setAcceptDialog(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Accept Estimate {estimate.estimate_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will mark the estimate as accepted. You can then convert it to an invoice.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setAcceptDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleAccept}
                        disabled={accepting}
                        startIcon={accepting ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: ACCENT, '&:hover': { bgcolor: '#16a34a' } }}
                    >
                        {accepting ? 'Accepting...' : 'Accept'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Convert to Invoice Confirmation Dialog */}
            <Dialog open={convertDialog} onClose={() => setConvertDialog(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Convert Estimate {estimate.estimate_number} to Invoice?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will create a new invoice from this estimate with all line items copied over. The estimate status will be updated to Converted.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConvertDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleConvert}
                        disabled={converting}
                        startIcon={converting ? <CircularProgress size={16} color="inherit" /> : <TransformIcon />}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}
                    >
                        {converting ? 'Converting...' : 'Convert to Invoice'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>Delete Estimate {estimate.estimate_number}?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontFamily, color: '#64748b' }}>
                        This will permanently delete the draft estimate and all its line items. This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialog(false)} sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleDelete}
                        disabled={deleting}
                        sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px', bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
